import { supabase } from './supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';
import {
  ArticleCommentRow,
  ArticleGenerationRow,
  BriefsPayload,
  GenerationSection,
  HeadingsPayload,
  LlmLogRow,
  ReviseSpanErrorCode,
  ReviseSpanRequest,
  ReviseSpanResponse,
  RevisionRow,
} from '../types/articleGeneration';

/** A revise_span refusal, with the backend's error code for the UI to explain. */
export class ReviseSpanError extends Error {
  code: ReviseSpanErrorCode;
  constructor(code: ReviseSpanErrorCode, message: string) {
    super(message);
    this.name = 'ReviseSpanError';
    this.code = code;
  }
}

const KNOWN_CODES: ReviseSpanErrorCode[] = ['span_mismatch', 'already_applied', 'revision_rejected'];

export const articleGenerationService = {
  listGenerations: async (companyId: string, domainId: string): Promise<ArticleGenerationRow[]> => {
    const { data, error } = await supabase
      .from('article_generations')
      .select('*')
      .eq('company_id', companyId)
      .eq('domain_id', domainId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as ArticleGenerationRow[];
  },

  getGeneration: async (id: string): Promise<ArticleGenerationRow | null> => {
    const { data, error } = await supabase
      .from('article_generations')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data as ArticleGenerationRow | null;
  },

  updateSections: async (id: string, sections: GenerationSection[]): Promise<void> => {
    const { error } = await supabase.from('article_generations').update({ sections }).eq('id', id);
    if (error) throw error;
  },

  updateStructure: async (id: string, structure: HeadingsPayload): Promise<void> => {
    const { error } = await supabase.from('article_generations').update({ structure }).eq('id', id);
    if (error) throw error;
  },

  updateBriefs: async (id: string, briefs: BriefsPayload): Promise<void> => {
    const { error } = await supabase.from('article_generations').update({ briefs }).eq('id', id);
    if (error) throw error;
  },

  listAllGenerations: async (companyId: string): Promise<ArticleGenerationRow[]> => {
    const { data, error } = await supabase
      .from('article_generations')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as ArticleGenerationRow[];
  },

  listRevisions: async (generationId: string): Promise<RevisionRow[]> => {
    const { data, error } = await supabase
      .from('article_generation_revisions')
      .select('*')
      .eq('generation_id', generationId)
      .order('target', { ascending: true })
      .order('round', { ascending: true });
    if (error) throw error;
    return (data ?? []) as RevisionRow[];
  },

  listComments: async (generationId: string): Promise<ArticleCommentRow[]> => {
    const { data, error } = await supabase
      .from('article_generation_comments')
      .select('*')
      .eq('generation_id', generationId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as ArticleCommentRow[];
  },

  addComment: async (
    generationId: string,
    sectionIndex: number | null,
    body: string
  ): Promise<ArticleCommentRow> => {
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const { data, error } = await supabase
      .from('article_generation_comments')
      .insert({
        generation_id: generationId,
        section_index: sectionIndex,
        body,
        created_by: userId,
      })
      .select()
      .single();
    if (error) throw error;
    return data as ArticleCommentRow;
  },

  deleteComment: async (id: string): Promise<void> => {
    const { error } = await supabase.from('article_generation_comments').delete().eq('id', id);
    if (error) throw error;
  },

  // Stop a run that continues server-side (chunked execution). Only a run that
  // is still generating can be cancelled; the pipeline stops at its next
  // checkpoint.
  cancelGeneration: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('article_generations')
      .update({ status: 'cancelled' })
      .eq('id', id)
      .eq('status', 'generating');
    if (error) throw error;
  },

  // Rewrite one highlighted passage from a note. The function answers JSON;
  // a 4xx carries an error code that becomes a ReviseSpanError.
  reviseSpan: async (request: ReviseSpanRequest): Promise<ReviseSpanResponse> => {
    const { data, error } = await supabase.functions.invoke('article-generation', {
      body: request,
    });
    if (error) {
      let code: ReviseSpanErrorCode = 'unknown';
      let message = error.message;
      if (error instanceof FunctionsHttpError) {
        const payload = await error.context.json().catch(() => ({}));
        if (KNOWN_CODES.includes(payload?.error)) code = payload.error;
        message = payload?.message || payload?.error || message;
      }
      throw new ReviseSpanError(code, message);
    }
    return data as ReviseSpanResponse;
  },

  listLlmLogs: async (generationId: string): Promise<LlmLogRow[]> => {
    const { data, error } = await supabase
      .from('article_generation_llm_logs')
      .select('*')
      .eq('generation_id', generationId)
      .order('sent_at', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as LlmLogRow[];
  },
};
